import { createContext, useContext, useEffect, useState } from 'react'
import { sharedMe } from 'lib/owner-notes/api'
import { hasDeviceMarker } from 'lib/owner-notes/declared'
import { isOwnMemberPage } from 'lib/community/model'
import { viewerMe } from 'lib/community/hub'
import type { OwnerMeResponse } from 'lib/dealhunt/types'

/**
 * The member hub's "View as visitor" switch (client state only, never in the HTML). While it is
 * on, `useOwnMemberMe` answers null, so every owner layer renders exactly the visitor page.
 * Outside a hub the default applies: off.
 */
export const MemberViewContext = createContext<{
  asVisitor: boolean
  setAsVisitor: (on: boolean) => void
}>({ asVisitor: false, setAsVisitor: () => undefined })

/**
 * `/me` when the reader is the member whose page this is (`isOwnMemberPage`), else null — and
 * null while the hub's "View as visitor" is on (`viewerMe`), unless `evenAsVisitor` (the one
 * control that switches the preview back). Reader-specific, so never in the shared-cached HTML:
 * it resolves after hydration, and only on a browser that holds an owner device. `sharedMe` makes
 * it one `/me` per page however many elements ask. `epoch`: bump it after a write (an appeal on
 * the Gear tab) to read `/me` again — every write already forgot the shared answer.
 */
export function useOwnMemberMe(
  handle: string,
  { evenAsVisitor = false, epoch = 0 }: { evenAsVisitor?: boolean; epoch?: number } = {}
): OwnerMeResponse | null {
  const { asVisitor } = useContext(MemberViewContext)
  const [me, setMe] = useState<OwnerMeResponse | null>(null)
  useEffect(() => {
    if (!hasDeviceMarker()) {
      return undefined
    }
    let live = true
    sharedMe().then(
      answer => {
        if (live && isOwnMemberPage(answer, handle)) {
          setMe(answer)
        }
      },
      () => undefined
    )
    return () => {
      live = false
    }
  }, [handle, epoch])
  return evenAsVisitor ? me : viewerMe(me, asVisitor)
}
